import React, { useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import {
    closePaymentModal,
    setModalStep,
    createVirtualAccount,
    resetCurrentPayment,
    clearError
} from '../../redux/slices/fidelityPaymentSlice';
import { useErrorLogging } from '../../hooks/useErrorLogging';
import './PaymentModal.css';

interface PaymentModalProps {
    amount?: number;
    paymentType?: 'funding' | 'payment';
    mode?: 'modal' | 'page';
    onSuccess?: (transactionRef: string) => void;
    onClose?: () => void;
}

const PaymentModal: FC<PaymentModalProps> = ({
    amount: initialAmount,
    paymentType = 'payment',
    mode = 'modal',
    onSuccess,
    onClose
}) => {
    const dispatch = useDispatch();
    const { logError, logInfo } = useErrorLogging();
    const {
        isModalOpen,
        modalStep,
        initializeLoading,
        initializeError,
        currentPayment
    } = useSelector((state: RootState) => state.fidelityPayment);

    // Form state
    const [amount, setAmount] = useState<string>(initialAmount ? initialAmount.toString() : '');
    const [description, setDescription] = useState<string>(paymentType === 'funding' ? 'Account Funding' : '');
    const [customerFirstName, setCustomerFirstName] = useState<string>('');
    const [customerLastName, setCustomerLastName] = useState<string>('');
    const [customerEmail, setCustomerEmail] = useState<string>('');
    const [customerMobile, setCustomerMobile] = useState<string>('');

    // In page mode, always render content regardless of modal state
    if (mode === 'modal' && !isModalOpen) return null;

    const handleAmountSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        const numericAmount = Number.parseFloat(amount);
        if (!amount || !description) {
            logError('Payment validation failed', new Error('Missing amount or description'));
            alert('Please fill in all fields');
            return;
        }
        if (paymentType === 'funding' && (Number.isNaN(numericAmount) || numericAmount < 100)) {
            logError('Payment validation failed', new Error('Amount below minimum'));
            alert('Minimum funding amount is NGN 100');
            return;
        }
        logInfo('Amount step submitted', { amount, description, paymentType });
        dispatch(setModalStep('customer'));
    };

    const handleCustomerSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        if (!customerFirstName || !customerLastName || !customerEmail || !customerMobile) {
            logError('Customer info validation failed', new Error('Missing customer information'));
            alert('Please fill in all customer information');
            return;
        }
        logInfo('Customer info step submitted', { 
            name: `${customerFirstName} ${customerLastName}`,
            email: customerEmail
        });
        dispatch(setModalStep('review'));
    };

    const handleProcessPayment = (): void => {
        const numericAmount = Number.parseFloat(amount);
        if (paymentType === 'funding' && (Number.isNaN(numericAmount) || numericAmount < 100)) {
            logError('Payment validation failed', new Error('Amount below minimum'));
            alert('Minimum funding amount is NGN 100');
            return;
        }
        logInfo('Creating virtual account', {
            amount: numericAmount,
            email: customerEmail,
            name: `${customerFirstName} ${customerLastName}`
        });
        dispatch(setModalStep('processing'));
        dispatch(createVirtualAccount({
            amount: numericAmount,
            customerEmail,
            customerFirstName,
            customerLastName,
            customerMobile,
            description
        }) as any);
    };

    const handleRetry = (): void => {
        handleProcessPayment();
    };

    const handleClose = (): void => {
        logInfo('Payment modal closed', { modalStep });
        dispatch(clearError());
        dispatch(resetCurrentPayment());
        dispatch(setModalStep('amount'));
        if (mode === 'modal') {
            dispatch(closePaymentModal());
        }
        setAmount('');
        setDescription(paymentType === 'funding' ? 'Account Funding' : '');
        setCustomerFirstName('');
        setCustomerLastName('');
        setCustomerEmail('');
        setCustomerMobile('');
        onClose?.();
    };

    const hasValidVirtualAccount = Boolean(
        currentPayment?.virtualAccount?.bankName &&
        currentPayment?.virtualAccount?.accountNumber &&
        currentPayment?.virtualAccount?.accountName
    );

    const content = (
        <div className="payment-modal">
            <div className="payment-modal-header">
                <h2 id="payment-modal-title">{paymentType === 'funding' ? 'Fund Account' : 'Initiate Payment'}</h2>
                <button className="close-btn" onClick={handleClose} aria-label="Close payment modal">×</button>
            </div>

            <div className="payment-modal-content">
            {/* Step 1: Amount */}
            {modalStep === 'amount' && (
                <form onSubmit={handleAmountSubmit} className="payment-form">
                    <div className="form-group">
                        <label htmlFor="amount-input">Amount (NGN)</label>
                        <input
                            id="amount-input"
                            type="number"
                            step="0.01"
                            min={paymentType === 'funding' ? 100 : 1}
                            value={amount}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                            placeholder="Enter amount"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="description-input">Description</label>
                        <textarea
                            id="description-input"
                            value={description}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                            placeholder="What is this payment for?"
                            rows={3}
                            required
                        />
                    </div>



                    <button type="submit" className="btn-primary">
                        Next
                    </button>
                </form>
            )}

            {/* Step 2: Customer Information */}
            {modalStep === 'customer' && (
                <form onSubmit={handleCustomerSubmit} className="payment-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="first-name">First Name</label>
                            <input
                                id="first-name"
                                type="text"
                                value={customerFirstName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomerFirstName(e.target.value)}
                                placeholder="First name"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="last-name">Last Name</label>
                            <input
                                id="last-name"
                                type="text"
                                value={customerLastName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomerLastName(e.target.value)}
                                placeholder="Last name"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="email-input">Email</label>
                        <input
                            id="email-input"
                            type="email"
                            value={customerEmail}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomerEmail(e.target.value)}
                            placeholder="Email address"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="mobile-input">Mobile Number</label>
                        <input
                            id="mobile-input"
                            type="tel"
                            value={customerMobile}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomerMobile(e.target.value)}
                            placeholder="Mobile number"
                            required
                        />
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => dispatch(setModalStep('amount'))}
                        >
                            Back
                        </button>
                        <button type="submit" className="btn-primary">
                            Review Payment
                        </button>
                    </div>
                </form>
            )}

            {/* Step 3: Review */}
            {modalStep === 'review' && (
                <div className="payment-review">
                    <h3>Review Payment Details</h3>
                    <div className="review-item">
                        <span>Amount:</span>
                        <strong>NGN {Number.parseFloat(amount).toLocaleString()}</strong>
                    </div>
                    <div className="review-item">
                        <span>Description:</span>
                        <strong>{description}</strong>
                    </div>
                    <div className="review-item">
                        <span>Customer:</span>
                        <strong>{customerFirstName} {customerLastName}</strong>
                    </div>
                    <div className="review-item">
                        <span>Email:</span>
                        <strong>{customerEmail}</strong>
                    </div>
                    <div className="review-item">
                        <span>Phone:</span>
                        <strong>{customerMobile}</strong>
                    </div>

                    <div className="form-actions">
                        <button
                            className="btn-secondary"
                            onClick={() => dispatch(setModalStep('customer'))}
                        >
                            Back
                        </button>
                        <button
                            className="btn-primary"
                            onClick={handleProcessPayment}
                            disabled={initializeLoading}
                        >
                            {initializeLoading ? 'Processing...' : 'Confirm Payment'}
                        </button>
                    </div>
                </div>
            )}

            {/* Step 4: Processing */}
            {modalStep === 'processing' && (
                <div className="payment-processing">
                    <div className="spinner"></div>
                    <h3>Processing Payment...</h3>
                    <p>Please wait while we process your payment</p>
                </div>
            )}

            {/* Step 5: Result */}
            {modalStep === 'result' && (
                <div className="payment-result">
                    {currentPayment?.success && hasValidVirtualAccount ? (
                        <>
                            <div className="success-icon">✓</div>
                            <h3>Virtual Account Created Successfully!</h3>
                            <div className="result-details">
                                <p>Transaction Reference: {currentPayment!.paymentId}</p>
                                <p>Status: {currentPayment!.status}</p>
                                {typeof currentPayment?.amount === 'number' && (
                                    <p>Amount: NGN {currentPayment.amount.toLocaleString()}</p>
                                )}
                                <div className="virtual-account-details">
                                    <h4>Bank Transfer Details:</h4>
                                    <p><strong>Bank Name:</strong> {currentPayment.virtualAccount.bankName}</p>
                                    <p><strong>Account Number:</strong> {currentPayment.virtualAccount.accountNumber}</p>
                                    <p><strong>Account Name:</strong> {currentPayment.virtualAccount.accountName}</p>
                                    {currentPayment.virtualAccount.reference && (
                                        <p><strong>Reference:</strong> {currentPayment.virtualAccount.reference}</p>
                                    )}
                                </div>
                                <p className="transfer-instruction">Please transfer the exact amount to the account details above. Your wallet will be credited automatically once the transfer is confirmed.</p>
                            </div>
                            <div className="form-actions">
                                <button
                                    className="btn-primary"
                                    onClick={handleClose}
                                >
                                    Done
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="error-icon">!</div>
                            <h3>Virtual Account Creation Failed</h3>
                            <div className="result-details">
                                <p>Status: {currentPayment?.status || 'Failed'}</p>
                                <p>Message: {currentPayment?.message || 'Virtual account creation failed'}</p>
                                {initializeError && <p className="error-text">{typeof initializeError === 'string' ? initializeError : JSON.stringify(initializeError)}</p>}
                            </div>
                            <div className="form-actions">
                                <button
                                    className="btn-secondary"
                                    onClick={handleClose}
                                >
                                    Close
                                </button>
                                <button
                                    className="btn-primary"
                                    onClick={handleRetry}
                                    disabled={initializeLoading}
                                >
                                    {initializeLoading ? 'Retrying...' : 'Retry'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
            </div>
        </div>
    );

    if (mode === 'page') {
        return (
            <div className="payment-modal-page">
                {content}
            </div>
        );
    }

    return (
        <div className="payment-modal-overlay">
            <div className="payment-modal-container">
                {content}
            </div>
        </div>
    );
};

export default PaymentModal;

